// library imports
import React, { useEffect, useState } from 'react';
import { Checkbox, Row, Col, Button, Space, Menu } from 'antd';
import styled from 'styled-components/macro';
import { useTranslation } from '../../i18n/useTranslation';

// components
import CreateGlossaryEntityModal from '../entity/shared/EntityDropdown/CreateGlossaryEntityModal';
import BulkLoadEntityModal from '../entity/shared/EntityDropdown/BulkLoadEntityModal';
import TabToolbar from '../entity/shared/components/styled/TabToolbar';
import EmptyGlossarySection from './EmptyGlossarySection';
import GlossaryEntitiesPath from './GlossaryEntitiesPath';
import GlossaryEntitiesList from './GlossaryEntitiesList';
import { Notification } from '../shared/Notification';
import { Message } from '../shared/Message';

// services
import {
    useGetRootGlossaryNodesQuery,
    useGetRootGlossaryTermsQuery,
    useGetEntityImportValidationMetadataQuery,
} from '../../graphql/glossary.generated';

import { useGlossaryEntityData } from '../entity/shared/GlossaryEntityContext';
import { useExportEntityMutation, useBatchRemoveEntitiesMutation } from '../../graphql/mutations.generated';
import { BrowseDropDown, BrowseMenu } from '../browse/BrowseResultActions';
import { sortGlossaryTerms } from '../entity/glossaryTerm/utils';
import { sortGlossaryNodes } from '../entity/glossaryNode/utils';
import { useUserContext } from '../context/useUserContext';
import { useEntityRegistry } from '../useEntityRegistry';
import { EntityType, EntityImportValidationMetadataInput } from '../../types.generated';
import {
    BUSINESS_GLOSSARY_CREATE_TERM_ID,
    BUSINESS_GLOSSARY_CREATE_TERM_GROUP_ID,
} from '../onboarding/config/BusinessGlossaryOnboardingConfig';
import { usePagination } from '../shared/usePagination';
import { bulkDeleteGlossaryEntities } from '../entity/glossaryNode/utils/bulkDeleteGlossaryEntities';
import { ComponentSpinner } from '../shared/ComponentSpinner';

// svg imports
import { ReactComponent as DownloadOutlined } from '../../images/downloaded-outline.svg';
import { ReactComponent as PlusOutlined } from '../../images/add-outline.svg';
import { ReactComponent as UploadOutlined } from '../../images/upload.svg';
import { ReactComponent as MoreAction } from '../../images/more-vert.svg';
import { ReactComponent as DeleteOutlined } from '../../images/delete-outline.svg';

const GREY_COLOR = '#5F767C';

export const HeaderWrapper = styled(TabToolbar)`
    padding: 12px 24px;
    height: auto;
`;

const GlossaryWrapper = styled(Row)`
    flex: 1;
`;

const ButtonWrapper = styled.div`
    gap: 8px;
    display: flex;
    margin-right: 8px;
    align-items: center;
`;

export const MAX_BROWSER_WIDTH = 500;
export const MIN_BROWSWER_WIDTH = 200;

const input: EntityImportValidationMetadataInput = {
    entityType: 'glossaryTerm',
};

function BusinessGlossaryPage() {
    const { t } = useTranslation();

    const {
        data: termsData,
        refetch: refetchForTerms,
        loading: termsLoading,
        error: termsError,
    } = useGetRootGlossaryTermsQuery();

    const { data: headers } = useGetEntityImportValidationMetadataQuery({ variables: { input } });

    const {
        data: nodesData,
        refetch: refetchForNodes,
        loading: nodesLoading,
        error: nodesError,
    } = useGetRootGlossaryNodesQuery();
    const entityRegistry = useEntityRegistry();
    const { setEntityData } = useGlossaryEntityData();

    useEffect(() => {
        setEntityData(null);
    }, [setEntityData]);

    const terms = termsData?.getRootGlossaryTerms?.terms.sort((termA, termB) =>
        sortGlossaryTerms(entityRegistry, termA, termB),
    );
    const nodes = nodesData?.getRootGlossaryNodes?.nodes.sort((nodeA, nodeB) =>
        sortGlossaryNodes(entityRegistry, nodeA, nodeB),
    );

    const hasTermsOrNodes = !!nodes?.length || !!terms?.length;

    const [isCreateTermModalVisible, setIsCreateTermModalVisible] = useState(false);
    const [isCreateNodeModalVisible, setIsCreateNodeModalVisible] = useState(false);
    const [isBulkLoadModalVisible, setIsBulkLoadModalVisible] = useState(false);
    const [isCsvExporting, setIsCsvExporting] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [exportEntity, setExportEntity]: any = useState(null);

    const glossaries = [...(nodes || []), ...(terms || [])];

    const {
        page,
        pageSize,
        selectedItems,
        isAllSelected,
        isIndeterminate,
        handleSelectAll,
        setSelectedItems,
        handleChangePage,
        handleShowSizeChange,
    } = usePagination({
        items: glossaries.map((glossary) => glossary.urn || ''),
    });

    const [exportEntityMutation] = useExportEntityMutation();
    const [batchRemoveGlossaryMutation] = useBatchRemoveEntitiesMutation();

    const user = useUserContext();
    const canManageGlossaries = user?.platformPrivileges?.manageGlossaries;

    /**
     * Handles the export of the selected glossary to a CSV file.
     * Calls the exportEntityMutation to export the selected glossary to a CSV file.
     * Displays a notification message to the user once the export is completed.
     * Clears the selectedItems state and sets the isAllSelected state to false.
     */
    const exportCsv = () => {
        const glossaryNodeUrns = nodes?.filter((item) => selectedItems.includes(item?.urn)).map((item) => item?.urn);
        const glossaryTermUrns = terms?.filter((item) => selectedItems.includes(item?.urn)).map((item) => item?.urn);

        setIsCsvExporting(true);
        setExportEntity(null);

        const exportInput = {
            glossaryTerm: {
                glossaryNodeUrns: glossaryNodeUrns || [],
                glossaryTermUrns: glossaryTermUrns || [],
            },
            entityType: 'glossaryTerm',
        };

        exportEntityMutation({
            variables: {
                input: exportInput,
            },
        })
            .then((res = {}) => {
                const { data } = res;
                setExportEntity(data?.exportEntity);
                setSelectedItems([]);
            })
            .catch((e) => {
                if (e.networkError.statusCode === 503) {
                    const updatedInput = {
                        ...exportInput,
                        async: true,
                    };
                    // Retry with a different input for 504 errors
                    exportEntityMutation({
                        variables: {
                            input: updatedInput,
                        },
                    }).then((retryRes) => {
                        const { data } = retryRes;
                        setExportEntity(data?.exportEntity);
                        setSelectedItems([]);
                    });
                }
            })
            .finally(() => setIsCsvExporting(false));
    };

    /**
     * Handles the bulk deletion of the selected glossary terms.
     * Uses the reusable bulkDeleteGlossaryEntities utility.
     */
    const handleBulkDelete = () => {
        bulkDeleteGlossaryEntities({
            nodes: nodes || [],
            terms: terms || [],
            selectedItems,
            batchRemoveGlossaryMutation,
            setSelectedItems,
            refetch: () => {
                refetchForTerms();
                refetchForNodes();
                return Promise.resolve();
            },
            onSuccess: () => {
                handleChangePage(1);
            },
            setIsDeleting,
        });
    };

    return (
        <>
            {exportEntity && <Notification exportEntity={exportEntity} />}

            {isCsvExporting && <Message type="loading" content={t('Loading...')} style={{ marginTop: '10%' }} />}
            {isDeleting && (
                <Message type="loading" content={t('Deleting glossary entities...')} style={{ marginTop: '10%' }} />
            )}

            <GlossaryWrapper wrap={false} className="f-mt-2-xs">
                {(termsError || nodesError) && (
                    <Message type="error" content={t('Failed to load glossary! An unexpected error occurred.')} />
                )}
                <Col flex={1}>
                    <GlossaryEntitiesPath />
                    <HeaderWrapper>
                        <Space size="middle" align="center">
                            <Checkbox
                                checked={isAllSelected()}
                                indeterminate={isIndeterminate()}
                                disabled={termsLoading || nodesLoading}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                            >
                                {t('Select All')}
                            </Checkbox>
                        </Space>
                        <Row>
                            <ButtonWrapper>
                                <Button
                                    type="text"
                                    aria-label={t('Bulk Load')}
                                    disabled={!canManageGlossaries}
                                    onClick={() => setIsBulkLoadModalVisible(true)}
                                >
                                    <UploadOutlined color={GREY_COLOR} />
                                    <span className="f-color-gray-s100">{t('Bulk Load')}</span>
                                </Button>
                                <Button
                                    type="text"
                                    aria-label={t('Add Term')}
                                    data-testid="add-term-button"
                                    disabled={!canManageGlossaries}
                                    id={BUSINESS_GLOSSARY_CREATE_TERM_ID}
                                    onClick={() => setIsCreateTermModalVisible(true)}
                                >
                                    <PlusOutlined color={GREY_COLOR} style={{ width: 12 }} />
                                    <span className="f-color-gray-s100">{t('Add Term')}</span>
                                </Button>
                                <Button
                                    type="text"
                                    aria-label={t('Add Term Group')}
                                    disabled={!canManageGlossaries}
                                    data-testid="add-term-group-button"
                                    id={BUSINESS_GLOSSARY_CREATE_TERM_GROUP_ID}
                                    onClick={() => setIsCreateNodeModalVisible(true)}
                                >
                                    <PlusOutlined color={GREY_COLOR} style={{ width: 12 }} />
                                    <span className="f-color-gray-s100">{t('Add Term Group')}</span>
                                </Button>
                            </ButtonWrapper>
                            <BrowseDropDown
                                overlay={
                                    <BrowseMenu>
                                        <Menu.Item
                                            key="1"
                                            onClick={exportCsv}
                                            tabIndex={0}
                                            disabled={selectedItems.length === 0}
                                            className="f-color-gray-s100"
                                            icon={<DownloadOutlined style={{ width: 16 }} color="#5F767C" />}
                                        >
                                            {t('Export')}
                                        </Menu.Item>
                                        <Menu.Item
                                            key="2"
                                            tabIndex={0}
                                            className="f-color-gray-s100"
                                            disabled={selectedItems.length === 0 || !canManageGlossaries}
                                            icon={<DeleteOutlined style={{ width: 14 }} color="#5F767C" />}
                                            onClick={handleBulkDelete}
                                        >
                                            {t('Delete')}
                                        </Menu.Item>
                                    </BrowseMenu>
                                }
                                trigger={['click']}
                            >
                                <Row align="middle" style={{ gap: 8 }} tabIndex={0}>
                                    <MoreAction
                                        data-testid="entity-header-dropdown"
                                        width={12}
                                        height={12}
                                        color={GREY_COLOR}
                                    />
                                    <span className="f-color-gray-s100">{t('Actions')}</span>
                                </Row>
                            </BrowseDropDown>
                        </Row>
                    </HeaderWrapper>

                    {(termsLoading || nodesLoading) && <ComponentSpinner />}

                    {hasTermsOrNodes && (
                        <GlossaryEntitiesList
                            page={page}
                            pageSize={pageSize}
                            nodes={nodes || []}
                            terms={terms || []}
                            selectedItems={selectedItems}
                            onChangePage={handleChangePage}
                            setSelectedItems={setSelectedItems}
                            onShowSizeChange={handleShowSizeChange}
                            onBulkDeleteEntities={handleBulkDelete}
                        />
                    )}
                    {!(termsLoading || nodesLoading) && !hasTermsOrNodes && (
                        <EmptyGlossarySection
                            title={t('Empty Glossary')}
                            description={t('CREATE_TERMS_TERM_GROUPS_ORGANIZE_DATA_DESC')}
                            refetchForTerms={refetchForTerms}
                            refetchForNodes={refetchForNodes}
                        />
                    )}
                </Col>
            </GlossaryWrapper>
            {isBulkLoadModalVisible && (
                <BulkLoadEntityModal
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsBulkLoadModalVisible(false)}
                    expectedHeadersFromApi={
                        headers?.getEntityImportValidationMetadata?.validationMetadata.map((header) => ({
                            ...header,
                            validate: header.validate === null ? undefined : header.validate,
                            sampleRows: header.sampleRows.map((row) => row.map((cell) => cell || '')),
                        })) || []
                    }
                />
            )}
            {isCreateTermModalVisible && (
                <CreateGlossaryEntityModal
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsCreateTermModalVisible(false)}
                    refetchData={refetchForTerms}
                />
            )}
            {isCreateNodeModalVisible && (
                <CreateGlossaryEntityModal
                    entityType={EntityType.GlossaryNode}
                    onClose={() => setIsCreateNodeModalVisible(false)}
                    refetchData={refetchForNodes}
                />
            )}
        </>
    );
}

export default BusinessGlossaryPage;
